import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import type { TablePaginationConfig } from "antd";
import { removeUnderscores } from "./stringUtils";

export const HEADER_HEIGHT = 64;

export const createPagination = (total: number): TablePaginationConfig => ({
  current: 1,
  pageSize: 25,
  showSizeChanger: true,
  pageSizeOptions: ["10", "25", "50", "100"],
  total,
});

export type ColumnLike = string | { field: string; label?: string };

/**
 * The unified /filters/ endpoint returns columns as {field, label} objects, while
 * many call sites still pass a bare field name. Accept both: passing an object
 * through used to reach removeUnderscores() and throw "e.replace is not a
 * function", which React Router turned into a full-screen error page.
 */
export const getColumnTitle = (
  column: ColumnLike,
  titleMap?: Record<string, string>,
) => {
  if (column && typeof column === "object") {
    if (column.label) return column.label;
    column = column.field;
  }

  if (typeof column !== "string") return "";

  const fallbackTitle = removeUnderscores(column)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  return titleMap?.[column] || fallbackTitle;
};

export const getColumnWidth = (
  column: string,
  titleMap?: Record<string, string>,
  exceptions?: Record<string, number>,
  columneTitleFunction?: (column: string) => string,
) => {
  if (exceptions?.[column]) {
    return exceptions[column];
  }

  if (columneTitleFunction) {
    return Math.max(100, 10 * (columneTitleFunction(column).length + 1));
  }

  return Math.max(100, 10 * (getColumnTitle(column, titleMap).length + 1));
};

export const useTableHeight = ({
  tableRef,
  reservedHeight = 100,
  minHeight = 200,
}: {
  tableRef: React.RefObject<HTMLDivElement>;
  reservedHeight?: number;
  minHeight?: number;
}) => {
  const getFallbackHeight = useCallback(
    () =>
      typeof window === "undefined"
        ? 0
        : Math.max(
            minHeight,
            window.innerHeight - HEADER_HEIGHT - reservedHeight,
          ),
    [minHeight, reservedHeight],
  );

  const [tableHeight, setTableHeight] = useState<number>(() =>
    getFallbackHeight(),
  );

  useLayoutEffect(() => {
    const updateHeight = () => {
      const rect = tableRef.current?.getBoundingClientRect();
      const height = rect
        ? Math.max(
            minHeight,
            Math.round(window.innerHeight - rect.top - reservedHeight),
          )
        : getFallbackHeight();
      // Measuring the DOM and storing the result is the whole point of this layout
      // effect; there is no render-time value to derive the height from.
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setTableHeight(height);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    // Content above the measured element (filter chips, toolbars, tab bars)
    // can appear, disappear, or wrap after mount; observing the body catches
    // those layout shifts, which a window resize listener alone would miss.
    const observer = new ResizeObserver(updateHeight);
    observer.observe(document.body);

    return () => {
      window.removeEventListener("resize", updateHeight);
      observer.disconnect();
    };
  }, [getFallbackHeight, minHeight, reservedHeight, tableRef]);

  return { tableHeight };
};

export const useSelectableTableView = <T,>() => {
  const [selectedRecord, setSelectedRecord] = useState<T | null>(null);

  const getClickableRowProps = useCallback(
    (record: T) => ({
      className: "cursor-pointer",
      onClick: () => setSelectedRecord(record),
    }),
    [],
  );

  return {
    selectedRecord,
    setSelectedRecord,
    getClickableRowProps,
  };
};

export const useOpenRecordFromParam = <T extends Record<string, unknown>>({
  paramName,
  rows,
  idField,
  setSelectedRecord,
}: {
  paramName: string;
  rows: T[];
  idField: keyof T;
  setSelectedRecord: (record: T) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParams.get(paramName);

  useEffect(() => {
    if (!paramValue) return;
    const record = rows.find((r) => r[idField] === paramValue);
    if (record) {
      setSelectedRecord(record);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(paramName);
          return next;
        },
        { replace: true },
      );
    }
  }, [
    paramValue,
    rows,
    idField,
    setSelectedRecord,
    setSearchParams,
    paramName,
  ]);
};
